import { Input } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useOmnisendCRUDActions } from '@mm/state/actions/OmnisendCRUDActions';
import { selectOmnisendConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useEffect, useState, MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import styles from './OmnisendIntegrations.module.css';
import { OmnisendSuppressionSetup } from './OmnisendSuppressionSetup';
import { OmnisendSyncSetup } from './OmnisendSyncSetup';

export const OmnisendSettings = () => {
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const accounts = useSelector((store: StoreState) => store.omnisend.accounts);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const omnisendConnections = useSelector(selectOmnisendConnections);
  const omnisendConnection = omnisendConnections?.[0];

  const accessToken = accounts.find(
    account => account.id === omnisendConnection?.accessToken?.id,
  );

  const [apiKey, setApiKey] = useState(accessToken?.accessToken);
  const [error, setError] = useState('');

  const disabledApiKey =
    omnisendConnection && apiKey === accessToken?.accessToken;

  const { getAccounts, createAccessToken, updateAcceessToken } =
    useOmnisendCRUDActions();
  const { update, create } = useConnectionCRUDActions();

  const { openNewBillingModal2023 } = useModalActions();
  const notMyToken = accessToken?.readOnly;

  const showUpgradeModal = (onSuccessBilling?: () => void) => {
    openNewBillingModal2023({
      landmineName: landmineNames.SENDLANE_UPGRADE,
      onSuccessBilling,
    });
  };

  const connectApiKey = async (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();

    if (!apiKey || apiKey.length < 60) {
      setError('Invalid API Key');
      return;
    }

    const createKey = async (accessToken: string) => {
      const { id } = await createAccessToken(activeFbPageId, accessToken);

      if (!omnisendConnections.length) {
        await create(activeFbPageId, {
          name: 'Omnisend Connection',
          connectionKind: 'omnisend',
          omnisendParams: { accessTokenId: id },
        });
      } else if (
        (omnisendConnections || []).find(conn => conn.accessToken?.id !== id)
      ) {
        omnisendConnections.forEach(connection => {
          update(activeFbPageId, {
            id: connection.id,
            omnisendParams: {
              accessTokenId: id,
            },
          });
        });
      }
    };

    if (!hasNativeIntegrationsFeature) {
      showUpgradeModal(connectApiKey);
    } else {
      if (accessToken) {
        updateAcceessToken(activeFbPageId, accessToken.id, apiKey);
      } else {
        createKey(apiKey);
      }
      setError('');
    }
  };

  useEffect(() => {
    getAccounts(activeFbPageId);
  }, []);

  useEffect(() => {
    setApiKey(accessToken?.accessToken);
  }, [accessToken]);

  useEffect(() => {
    if (
      accessToken &&
      !omnisendConnections.find(conn => conn.audienceId === null)
    ) {
      create(activeFbPageId, {
        name: 'Omnisend Connection',
        connectionKind: 'omnisend',
        omnisendParams: { accessTokenId: accessToken.id },
      });
    }
  }, [accessToken, omnisendConnections]);

  return (
    <div className={styles.settingsContent}>
      <div>
        <span className={styles.label}>Credentials</span>

        <div className={classNames(styles.box, styles.boxWithHelp)}>
          <div className={styles.boxContent}>
            <div className={styles.heading}>API Key</div>
            <div className={styles.apiKeyInput}>
              <Input
                placeholder="Enter your API Key"
                css={{ container: styles.inputContainer, input: styles.input }}
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
              />
              <PrimaryButton
                className={styles.primaryButton}
                onClick={e => connectApiKey(e)}
                disabled={disabledApiKey}
              >
                {omnisendConnection && disabledApiKey
                  ? 'Connected'
                  : omnisendConnection
                  ? 'Update'
                  : 'Connect'}
              </PrimaryButton>
            </div>
            <div className={styles.error}>{error}</div>
          </div>
        </div>
      </div>
      {accessToken && (
        <>
          <div>
            <span className={styles.label}>Suppressions</span>
            <OmnisendSuppressionSetup tokenId={accessToken.id} />
          </div>
          <div>
            <span className={styles.label}>Destinations</span>
            <OmnisendSyncSetup
              accessToken={accessToken}
              controlsDisabled={!!notMyToken}
            />
          </div>
        </>
      )}
    </div>
  );
};
